import { useRef } from "react";
import * as yup from "yup";
import { Formik, Form as FormikForm } from "formik";
import { Button as BSButton, FloatingLabel, Form } from "react-bootstrap";
import { PaymentModel } from "../../../data/entities";
import { sharedProperty } from "../../../hooks";
import { formatDate, toYearMonthDayString } from "../../../utils/dateUtils";
import { formatPrice } from "../../../utils";
import { VoidPreAuthRequestDTO } from "../../../data/entities/payment";
import {
  Button,
  Icon,
  IconDefinitions,
  SlideOutModal,
  SlideOutModalProps,
} from "../../../components";

export interface VoidPreAuthModalProps extends SlideOutModalProps {
  onSubmit: (voidRequest: VoidPreAuthRequestDTO) => void;
  selectedPaymentState: sharedProperty<PaymentModel>;
}

export function VoidPreAuthModal(props: Readonly<VoidPreAuthModalProps>) {
  const submitRef = useRef<HTMLButtonElement>(null);

  type PaymentDetail = {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    className?: string;
  };
  
  const validationSchema = yup.object().shape({
    reason: yup.string().nullable(),
    confirmCancel: yup
      .boolean()
      .oneOf([true], "You must confirm before proceeding."),
  });

  const paymentDetails: Record<string, PaymentDetail> = {
    customer: { 
      icon: <Icon iconName={IconDefinitions.user} className="me-1" />,
      label: "Customer", 
      value: props.selectedPaymentState.value.invoice?.customer 
        ? `${props.selectedPaymentState.value.invoice.customer.firstName ?? ""} ${props.selectedPaymentState.value.invoice.customer.lastName ?? ""}`.trim()
        : "N/A"
    },
    bookingId: { 
      icon: <Icon iconName={IconDefinitions.clipboard} className="me-1" />,
      label: "Booking ID", 
      value: props.selectedPaymentState.value.invoice?.bookings && props.selectedPaymentState.value.invoice.bookings.length > 0 ? props.selectedPaymentState.value.invoice.bookings[0].id : "N/A" 
    },
    amount: { 
      icon: <Icon iconName={IconDefinitions.moneyBill} className="me-1" />,
      label: "Pre-Auth Amount", 
      value: formatPrice(props.selectedPaymentState.value.total ?? 0),
      className: "fw-bold" 
    },
    status: { 
      icon: <Icon iconName={IconDefinitions.infoCircle} className="me-1" />,
      label: "Status", 
      value: <span className="badge bg-warning text-dark">{props.selectedPaymentState.value.status}</span> 
    },
    date: { 
      icon: <Icon iconName={IconDefinitions.calendar} className="me-1" />,
      label: "Date", 
      value: props.selectedPaymentState.value.createdDate 
        ? formatDate(toYearMonthDayString(new Date(props.selectedPaymentState.value.createdDate.toLocaleString())) )
        : "N/A" 
    },
  };
  return (
    <SlideOutModal
      {...props}
      title="Cancel Pre-Authorization"
      footer={
        <div className="container p-0">
          <div className="row g-0">
            <div className="col-12 pe-2">
              <Button
                onClick={() => props.showState.setter(false)}
                variant="cancel"
                className="w-100"
              >
                Close
              </Button>
            </div>
            <div className="col-12 ps-2">
              <Button
                onClick={() => submitRef.current?.click()}
                variant="primary"
                className="w-100"
              >
                Confirm Cancel
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <Formik
        validationSchema={validationSchema}
        initialValues={{ 
          reason: "", 
          confirmCancel: false 
        }}
        onSubmit={(values) => {
          const voidRequest: VoidPreAuthRequestDTO = {
            paymentId: props.selectedPaymentState.value.id ?? 0,
            CancellationReason: values.reason ? values.reason : null
          };
          props.onSubmit(voidRequest);
          props.showState.setter(false);
        }}
      >
        {({ handleSubmit, handleChange, values, touched, errors }) => (
          <FormikForm noValidate onSubmit={handleSubmit}>
            {/* Payment Details */}
            <div className="mb-3">
              <div className="bg-primary bg-opacity-10 rounded-top px-3 py-2">
                <h6 className="fw-semibold text-primary m-0">Payment Details</h6>
              </div>
              <div className="border border-top-0 rounded-bottom bg-white">
                {Object.entries(paymentDetails).map(([key, detail], idx, arr) => (
                  <div
                    key={key}
                    className={`d-flex flex-column flex-md-row justify-content-between py-2 px-3 ${
                      idx < arr.length - 1 ? "border-bottom" : "" // To avoid a border for the last row 
                    }`}
                  >
                    <span className="text-muted fw-semibold mb-1 mb-md-0">
                      {detail.icon}{detail.label}:
                    </span>
                    <span
                      className={detail.className || "text-truncate"}
                      style={{ maxWidth: "100%" }}
                      title={typeof detail.value === "string" ? detail.value : undefined}
                    >
                      {detail.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            {/* Optional reason */}
            <div className="mb-3">
              <FloatingLabel
                controlId="cancelReason"
                label="Reason for cancellation (optional)"
              >
                <Form.Control
                  as="textarea"
                  name="reason"
                  value={values.reason}
                  onChange={handleChange}
                  style={{ minHeight: "100px" }}
                />
              </FloatingLabel>
            </div>

            {/* Confirmation checkbox */}
            <div className="mb-3 p-3 rounded bg-warning bg-opacity-10 border border-warning">
              <Form.Check
                id="confirmCancel"
                name="confirmCancel"
                type="checkbox"
                label="I understand that canceling this pre-authorization will release the hold and the customer will not be charged."
                checked={values.confirmCancel}
                onChange={handleChange}
                isInvalid={touched.confirmCancel && !!errors.confirmCancel}
                feedback={errors.confirmCancel}
                feedbackType="invalid"
              />
            </div>

            {/* Hidden submit trigger */}
            <BSButton ref={submitRef} type="submit" className="d-none">
              Submit
            </BSButton>
          </FormikForm>
        )}
      </Formik>
    </SlideOutModal>
  );
}

export default VoidPreAuthModal;
